import { api } from '../../lib/apiClient';

export type AuthMode = 'sigil' | 'oidc';

export type AuthConfig = {
  mode?: AuthMode;
  type?: string;
  issuer?: string;
  jwks_url?: string;
  audience?: string;
  user_id_claim?: string;
  email_claim?: string;
  required_claims?: Record<string, string[]>;
  jwks_refresh_interval_seconds?: number;
};

export type SigningMode = 'client_side' | 'tee';

export type Organization = {
  id: string;
  name: string;
  slug: string;
  country?: string;
  status: string;
  signing_mode: SigningMode;
  auth_config: AuthConfig;
  allowed_origins: string[];
  supported_networks: string[];
  recovery_cooldown_seconds: number;
  recovery_mode: RecoveryMode;
  logo_url?: string;
  brand_color?: string;
  support_email?: string;
  created_at: string;
};

export async function listMyOrgs(): Promise<Organization[]> {
  const res = await api.get<{ data: Organization[] }>('/v1/orgs');
  return res.data.data;
}

export async function getOrg(orgId: string): Promise<Organization> {
  const res = await api.get<{ data: Organization }>(`/v1/orgs/${orgId}`);
  return res.data.data;
}

export async function createOrg(input: {
  name: string;
  slug: string;
  country?: string;
  signing_mode?: SigningMode;
}): Promise<Organization> {
  const res = await api.post<{ data: Organization }>('/v1/orgs', input);
  return res.data.data;
}

export async function updateOrgAuth(
  orgId: string,
  input: { auth_config: AuthConfig },
): Promise<Organization> {
  const res = await api.patch<{ data: Organization }>(`/v1/orgs/${orgId}/auth`, input);
  return res.data.data;
}

export type RecoveryMode = 'customer_managed' | 'sigil_managed';

export async function updateOrgRecoveryMode(orgId: string, mode: RecoveryMode): Promise<Organization> {
  const res = await api.patch<{ data: Organization }>(`/v1/orgs/${orgId}/recovery-mode`, { mode });
  return res.data.data;
}

export async function updateOrgSettings(
  orgId: string,
  input: {
    name: string;
    logo_url: string;
    brand_color: string;
    support_email: string;
    allowed_origins: string[];
  },
): Promise<Organization> {
  const res = await api.patch<{ data: Organization }>(`/v1/orgs/${orgId}`, input);
  return res.data.data;
}

export async function updateSupportedNetworks(
  orgId: string,
  networks: string[],
): Promise<Organization> {
  const res = await api.patch<{ data: Organization }>(
    `/v1/orgs/${orgId}/supported-networks`,
    { supported_networks: networks },
  );
  return res.data.data;
}
